import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { ASSETS } from '@/constants';
import { guid } from '@/mixins/guid';
import { queuedWork } from '@/mixins/queued-work';
import TopControlBar from './top-control-bar';
import DropletsTab from './droplets-tab';
import BlocksContainer from './blocks-container';
import CanvasEditor from './canvas';
import StyleSidebar from './style-sidebar';
import SaveAlert from './save-alert';
import PageLoader from './page-loader';
import CartSlideout from './cart-slideout';

export default Vue.component('page-main', {
  template: require('./template.html'),
  name: 'page-main',
  mixins: [guid, queuedWork],
  components: {
    TopControlBar,
    DropletsTab,
    BlocksContainer,
    CanvasEditor,
    SaveAlert,
    StyleSidebar,
    PageLoader,
    CartSlideout,
  },
  computed: {
    ...mapGetters([
      'currentSite',
      'currentPage',
      'account',
      'websites',
      'viewport',
      'editorLoader',
      'activeDroplet',
      'globalToolBarOpen',
      'globalLoading',
    ]),
    type() {
      return this.currentPage.type
        ? this.currentPage.type.split('.')[0] || this.currentPage.type
        : null;
    },
    isFullPage() {
      return this._pageTypes.includes(this.type);
    },
    pageWidth() {
      return {
        width: this.collapsedControls ? '95%' : null,
      };
    },
    isCartSlideout() {
      return this.currentPage._id === 'cart-slideout';
    },
    isCanvasType() {
      const p = this.type;
      return p === 'droplet' || p === 'puddle' || p === 'sectionBlock';
    },
    collapsedControls() {
      return this.viewport === 'desktop' && !this.activeDroplet._id;
    },
  },
  watch: {
    'globalLoading.websites': function siteLoaded(loading) {
      if (!loading && !this.pagesLoaded) {
        this.loadPage();
      }
    },
  },
  data() {
    return {
      pageName: 'Page Main',
      pageLoaded: false,
    };
  },
  created() {
    this.loadPage();
  },
  destroyed() {
    // this triggers when a user leaves the editor
    this.$store.dispatch('setOriginalPage', { blocks: [] });
    this.$store.dispatch('setCurrentPage', { blocks: [] });
  },
  methods: {
    async loadPage() {
      // if app is being reloaded and websites are still loading, need to
      // return and allow for a reload of this function once done.
      if (this.globalLoading.websites) {
        return;
      }
      // in the event app reloads, and is brought back to this route, we
      // need to reset current site
      if (!this.currentSite._id) {
        const _id = this.$route.params.id;
        const site = _.find(this.websites, { _id });
        if (site) {
          await this.$store.dispatch('setCurrentSite', site);
        } else {
          // if the site is no longer there. route back to all sites
          this.$router.push({ path: '/websites' });
        }
      }
      // we need this in place to give picazzo a heads up that
      // it is loaded in the editor and to prefix any assets
      // with public urls. it's important this loads before blocks
      window.assetPrefix = this._servePrefix('');
      const _id = window.localStorage.getItem('lastPageEdited');
      // check if site items have been hydrated
      if (typeof this.currentSite.pages[0] === 'string') {
        await this.hydrateWebsiteData();
      }
      // If we get more than 1 element then we know that the _id property
      // and the homepage matched (because we'll always have isHomepage one)
      //
      // So we can use this fact to reduce the array to 1 item which is what
      // we're after we remove all the non candidates
      const dynamicPages = this.getDynamicPages();
      const recycled = this.currentSite.recycled || [];
      const allTypes = this.currentSite.pages.concat(
        this.currentSite.templates,
        this.currentSite.drafts,
        recycled,
        dynamicPages,
      );
      const pages = _.filter(
        allTypes,
        pg => pg._id === _id || (pg.isHomepage && pg.type === 'page'),
      );

      const lastPageEdited = pages.length === 2 ? _.find(pages, { _id }) : pages[0];

      const { item } = await this.checkQueuedWork({ page: lastPageEdited });

      this.$store.dispatch('setOriginalPage', item);
      this.$store.dispatch('setCurrentPage', item);
      // we may want to check if there is a sitemap loaded from the db first before we
      // regenerate it like this everytime
      this.$store.dispatch('setSiteMap');
      this.pageLoaded = true;
    },
    async hydrateWebsiteData() {
      this.$store.dispatch('setGlobalValue', {
        item: this.editorLoader,
        key: 'page',
        value: true,
      });
      try {
        const { website } = await this.$lightning([{
          name: 'hydrateWebsiteData',
          payload: {
            siteId: this.currentSite._id,
          },
        }]);
        this.$log.info('Hydrated data needed: ', website);
        // current site state needs updating here.
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key: 'pages',
          value: website.pages,
        });
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key: 'drafts',
          value: website.drafts,
        });
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key: 'templates',
          value: website.templates,
        });
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key: 'recycled',
          value: website.recycled,
        });
        if (website.dynamic) {
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite,
            key: 'dynamic',
            value: website.dynamic,
          });
        }
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        this.$store.dispatch('addNotification', {
          id: Date.now(),
          type: 'error',
          message,
        });
      } finally {
        this.$store.dispatch('setGlobalValue', {
          item: this.editorLoader,
          key: 'page',
          value: false,
        });
      }
    },
    getDynamicPages() {
      if (!this.currentSite.dynamic) {
        return [];
      }
      const pages = [];
      _.forOwn(this.currentSite.dynamic, (data) => {
        (data.pages || []).forEach((page) => {
          pages.push(page);
        });
      });
      return pages;
    },
    getFont(font) {
      const prefix = this.$demo ? `${ASSETS}live-demo/` : `${this._servePrefix('')}/`;
      return `${prefix}_fonts/${font}/font.css`;
    },
    onBodyClick() {
      this.$store.dispatch('setPreviewOptions', false);
      this.$store.dispatch('setSaveOptions', false);
      this.$store.dispatch('toggleHistory', false);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/index.js